import { useMemo } from 'react'

import { ChainId } from '@src/configs/chainInfo'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { IExchangeData } from '@src/types/exchange'
import { IToken } from '@src/types/token'
import { isFeatureEnabled } from '@src/utils/environment'
import { useTokensStore } from '@src/zustand_stores'

export const filterTokens = (
    availableTokens: IToken[],
    chainId: string | number,
    isBuySide = false,
    sellData: IExchangeData,
    buyData: IExchangeData
) => {
    // First filter by chain ID - ensure strict number comparison
    let availableTokensTemp = availableTokens.filter(token => {
        const tokenChainId = Number(token.chainId)
        const selectedChainId = Number(chainId)
        return tokenChainId === selectedChainId
    })

    if (!isFeatureEnabled('TRON_USDT_ONLY')) {
        availableTokensTemp = availableTokensTemp.filter(
            token => token.chainId !== ChainId.Tron || token.symbol === 'USDT'
        )
    }

    // FE-382: Filter out same token on same chain
    const availableTokensExcludingSame = availableTokensTemp.filter(token => {
        if (isBuySide) {
            return token.chainId === sellData.chainId ? token.address !== sellData.token.address : true
        } else {
            return token.chainId === buyData.chainId ? token.address !== buyData.token.address : true
        }
    })

    // Sort tokens by market cap
    return availableTokensExcludingSame.sort((a, b) => {
        const aCap = a.market_cap ?? -Infinity
        const bCap = b.market_cap ?? -Infinity
        return bCap - aCap
    })
}

export const useAvailableTokens = (chainId: string | number, isSolanaSwap: boolean, isBuySide = false) => {
    const { tokens: availableTokens } = useTokensStore()
    const { sellData, buyData } = useExchangeDataContext()

    const tokens = useMemo(() => {
        if (!chainId) return []
        return filterTokens(availableTokens, chainId, isBuySide, sellData, buyData)
    }, [chainId, availableTokens, isSolanaSwap, isBuySide, sellData, buyData])

    return { tokens }
}
